import React, { useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Input, 
  Button, 
  Select, 
  Space, 
  Tag, 
  Avatar, 
  Modal, 
  message,
  Pagination,
  Empty,
  Checkbox
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  MailOutlined,
  EyeOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  UserOutlined,
  FileExcelOutlined,
  ShareAltOutlined,
  ExportOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { contactsAPI, familiesAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import ContactForm from '../../components/Contacts/ContactForm';
import EmailModal from '../../components/Emails/EmailModal';
import ImportModal from '../../components/Contacts/ImportModal';
import BulkShareModal from '../../components/Shares/BulkShareModal';

const { Search } = Input;
const { Option } = Select;
const { Meta } = Card;

const Contacts = () => {
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEmailModalVisible, setIsEmailModalVisible] = useState(false);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [isBulkShareModalVisible, setIsBulkShareModalVisible] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [editingContact, setEditingContact] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: contactsData, isLoading } = useQuery(
    ['contacts', currentPage, searchTerm, selectedFamily],
    () => contactsAPI.getAll({
      page: currentPage,
      limit: 12,
      search: searchTerm,
      family_id: selectedFamily
    })
  );

  const { data: familiesData } = useQuery('families', () => familiesAPI.getAll());

  const deleteContactMutation = useMutation(contactsAPI.delete, {
    onSuccess: () => {
      message.success('Contact supprimé avec succès');
      queryClient.invalidateQueries('contacts');
    },
    onError: () => {
      message.error('Erreur lors de la suppression du contact');
    }
  });

  const contacts = contactsData?.data?.data?.contacts || [];
  const pagination = contactsData?.data?.data?.pagination || {};
  const families = familiesData?.data?.data?.families || [];

  const handleDelete = (contact) => {
    Modal.confirm({
      title: 'Êtes-vous sûr de vouloir supprimer ce contact?',
      content: `${contact.first_name} ${contact.last_name}`,
      okText: 'Oui',
      cancelText: 'Non',
      onOk: () => deleteContactMutation.mutate(contact.id)
    });
  };

  const handleSelectContact = (contact) => {
    setSelectedContacts(prev => {
      const isSelected = prev.some(c => c.id === contact.id);
      if (isSelected) {
        return prev.filter(c => c.id !== contact.id);
      } else {
        return [...prev, contact];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedContacts.length === contacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(contacts);
    }
  };

  const handleBulkExport = async () => {
    try {
      const contactIds = selectedContacts.map(contact => contact.id);
      const response = await contactsAPI.export(contactIds);
      
      // Create blob from response
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      // Get filename from response headers or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'contacts_export.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success(`${selectedContacts.length} contact(s) exporté(s) avec succès`);
      setSelectedContacts([]);
    } catch (error) {
      console.error('Export error:', error);
      message.error('Erreur lors de l\'exportation des contacts');
    }
  };

  const handleBulkDelete = async () => {
    Modal.confirm({
      title: 'Êtes-vous sûr de vouloir supprimer ces contacts?',
      content: `Vous êtes sur le point de supprimer ${selectedContacts.length} contact(s). Cette action est irréversible.`,
      okText: 'Oui, supprimer',
      cancelText: 'Annuler',
      okType: 'danger',
      onOk: async () => {
        try {
          const contactIds = selectedContacts.map(contact => contact.id);
          const response = await contactsAPI.bulkDelete(contactIds);
          
          if (response.data.success) {
            const { successful, failed } = response.data.data.summary;
            
            if (failed === 0) {
              message.success(`${successful} contact(s) supprimé(s) avec succès`);
            } else {
              message.warning(`${successful} contact(s) supprimé(s), ${failed} échec(s)`);
            }
            
            setSelectedContacts([]);
            queryClient.invalidateQueries('contacts');
          }
        } catch (error) {
          console.error('Bulk delete error:', error);
          message.error('Erreur lors de la suppression des contacts');
        }
      }
    });
  };

  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedContacts([]);
  };

  const renderContactCard = (contact) => {
    const isSelected = selectedContacts.some(c => c.id === contact.id);
    
    return (
      <Card
        key={contact.id}
        className={`contact-card ${isSelected ? 'selected' : ''}`}
        size="small"
        hoverable
        style={{
          border: isSelected ? '2px solid #1890ff' : undefined,
          position: 'relative'
        }}
        actions={[
          selectionMode && (
            <Checkbox
              key="select"
              checked={isSelected}
              onChange={() => handleSelectContact(contact)}
            />
          ),
          <EyeOutlined 
            key="view" 
            onClick={() => navigate(`/contacts/${contact.id}`)}
            title="Voir"
          />,
          <EditOutlined 
            key="edit" 
            onClick={() => {
              setEditingContact(contact);
              setIsModalVisible(true);
            }}
            title="Modifier"
          />,
          <MailOutlined 
            key="email" 
            onClick={() => {
              setSelectedContacts([contact]);
              setIsEmailModalVisible(true);
            }}
            title="Envoyer email"
          />,
          <DeleteOutlined 
            key="delete" 
            onClick={() => handleDelete(contact)}
            title="Supprimer"
          />
        ].filter(Boolean)}
      >
        <Meta
          avatar={
            contact.image_url ? (
              <Avatar src={contact.image_url} />
            ) : (
              <Avatar icon={<UserOutlined />} />
            )
          }
          title={
            <div>
              {contact.first_name} {contact.last_name}
              {contact.subFamily && (
                <Tag 
                  color={contact.subFamily.family?.color} 
                  style={{ marginLeft: 8 }}
                >
                  {contact.subFamily.family?.name}
                </Tag>
              )}
            </div>
          }
          description={
            <div>
              {contact.company && <div><strong>{contact.company}</strong></div>}
              {contact.position && <div>{contact.position}</div>}
              {contact.email && <div>{contact.email}</div>}
              {contact.phone && <div>{contact.phone}</div>}
            </div>
          }
        />
      </Card>
    );
  };

  const renderContactList = (contact) => {
    const isSelected = selectedContacts.some(c => c.id === contact.id);
    
    return (
      <Card
        key={contact.id}
        size="small"
        style={{ 
          marginBottom: 8,
          border: isSelected ? '2px solid #1890ff' : undefined
        }}
        hoverable
        onClick={() => navigate(`/contacts/${contact.id}`)}
      >
        <Row align="middle" justify="space-between">
          <Col>
            <Space>
              {selectionMode && (
                <Checkbox
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleSelectContact(contact);
                  }}
                />
              )}
              {contact.image_url ? (
                <Avatar src={contact.image_url} />
              ) : (
                <Avatar icon={<UserOutlined />} />
              )}
              <div>
                <div>
                  <strong>{contact.first_name} {contact.last_name}</strong>
                  {contact.subFamily && (
                    <Tag 
                      color={contact.subFamily.family?.color} 
                      style={{ marginLeft: 8 }}
                    >
                      {contact.subFamily.family?.name}
                    </Tag>
                  )}
                </div>
                {contact.company && <div style={{ color: '#666' }}>{contact.company}</div>}
                {contact.email && <div style={{ color: '#666' }}>{contact.email}</div>}
              </div>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button 
                icon={<MailOutlined />} 
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedContacts([contact]);
                  setIsEmailModalVisible(true);
                }}
              />
              <Button 
                icon={<EditOutlined />} 
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingContact(contact);
                  setIsModalVisible(true);
                }}
              />
              <Button 
                icon={<DeleteOutlined />} 
                danger
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(contact);
                }}
              />
            </Space>
          </Col>
        </Row>
      </Card>
    );
  };

  return (
    <div>
      <div className="page-header">
        <Row justify="space-between" align="middle">
          <Col>
            <h2>Contacts</h2>
          </Col>
          <Col>
            <Space>
              <Button
                type={viewMode === 'grid' ? 'primary' : 'default'}
                icon={<AppstoreOutlined />}
                onClick={() => setViewMode('grid')}
              />
              <Button
                type={viewMode === 'list' ? 'primary' : 'default'}
                icon={<UnorderedListOutlined />}
                onClick={() => setViewMode('list')}
              />
              <Button 
                icon={<FileExcelOutlined />}
                onClick={() => setIsImportModalVisible(true)}
              >
                Importer Excel
              </Button>
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingContact(null);
                  setIsModalVisible(true);
                }}
              >
                Ajouter un contact
              </Button>
              {selectedContacts.length > 0 && (
                <>
                  <Button 
                    icon={<MailOutlined />}
                    onClick={() => setIsEmailModalVisible(true)}
                  >
                    Envoyer email ({selectedContacts.length})
                  </Button>
                  <Button 
                    icon={<ShareAltOutlined />}
                    onClick={() => setIsBulkShareModalVisible(true)}
                  >
                    Partager ({selectedContacts.length})
                  </Button>
                  <Button 
                    icon={<ExportOutlined />}
                    onClick={handleBulkExport}
                  >
                    Exporter Excel ({selectedContacts.length})
                  </Button>
                  <Button 
                    icon={<DeleteOutlined />}
                    danger
                    onClick={handleBulkDelete}
                  >
                    Supprimer ({selectedContacts.length})
                  </Button>
                </>
              )}
            </Space>
          </Col>
        </Row>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder="Rechercher un contact..."
              allowClear
              onSearch={setSearchTerm}
              onChange={(e) => !e.target.value && setSearchTerm('')}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filtrer par famille"
              allowClear
              style={{ width: '100%' }}
              onChange={setSelectedFamily}
            >
              {families.map(family => (
                <Option key={family.id} value={family.id}>
                  {family.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Button
              type={selectionMode ? 'primary' : 'default'}
              onClick={toggleSelectionMode}
              style={{ 
                width: '100%',
                backgroundColor: selectionMode ? '#1890ff' : undefined,
                borderColor: '#1890ff',
                color: selectionMode ? undefined : '#1890ff'
              }}
            >
              {selectionMode ? 'Quitter sélection' : 'Sélectionner'}
            </Button>
          </Col>
          {selectionMode && contacts.length > 0 && (
            <Col xs={24} sm={12} md={6}>
              <Space>
                <Checkbox
                  checked={selectedContacts.length === contacts.length && contacts.length > 0}
                  indeterminate={selectedContacts.length > 0 && selectedContacts.length < contacts.length}
                  onChange={handleSelectAll}
                >
                  Tout sélectionner ({selectedContacts.length}/{contacts.length})
                </Checkbox>
                <Button
                  size="small"
                  onClick={() => setSelectedContacts([])}
                  disabled={selectedContacts.length === 0}
                >
                  Effacer
                </Button>
              </Space>
            </Col>
          )}
        </Row>
      </Card>

      {contacts.length === 0 ? (
        <Empty description="Aucun contact trouvé" />
      ) : viewMode === 'grid' ? (
        <Row gutter={[16, 16]}>
          {contacts.map(renderContactCard)}
        </Row>
      ) : (
        <div>
          {contacts.map(renderContactList)}
        </div>
      )}

      {pagination.pages > 1 && (
        <Row justify="center" style={{ marginTop: 24 }}>
          <Pagination
            current={currentPage}
            total={pagination.total}
            pageSize={pagination.limit}
            onChange={setCurrentPage}
            showSizeChanger={false}
            showQuickJumper
            showTotal={(total) => `${total} contacts`}
          />
        </Row>
      )}

      <Modal
        title={editingContact ? 'Modifier le contact' : 'Ajouter un contact'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingContact(null);
        }}
        footer={null}
        width={600}
      >
        <ContactForm
          contact={editingContact}
          onSuccess={() => {
            setIsModalVisible(false);
            setEditingContact(null);
            queryClient.invalidateQueries('contacts');
          }}
        />
      </Modal>

      <EmailModal
        visible={isEmailModalVisible}
        contacts={selectedContacts}
        onCancel={() => {
          setIsEmailModalVisible(false);
          setSelectedContacts([]);
        }}
        onSuccess={() => {
          setIsEmailModalVisible(false);
          setSelectedContacts([]);
          queryClient.invalidateQueries('email-logs');
        }}
      />

      <ImportModal
        visible={isImportModalVisible}
        onCancel={() => setIsImportModalVisible(false)}
        onSuccess={() => {
          setIsImportModalVisible(false);
          queryClient.invalidateQueries('contacts');
        }}
      />

      <BulkShareModal
        contacts={selectedContacts}
        visible={isBulkShareModalVisible}
        onClose={() => {
          setIsBulkShareModalVisible(false);
          setSelectedContacts([]);
        }}
      />
    </div>
  );
};

export default Contacts;
